import { create } from 'zustand'

import type {
  CreditAccount,
  DurationPackage,
  LedgerRecord,
  Order,
  PaymentChannel,
  TranscriptTask,
} from '@/entities/models'
import type { OrderListQuery, TaskListQuery } from '@/shared/api/contracts/repositories'
import { commerceRepository, creditRepository, taskRepository } from '@/shared/api/repositories'
import { createUuid } from '@/shared/lib/uuid'

const requestKey = (scope: string) => `${scope}-${createUuid()}`

interface BusinessState {
  packages: DurationPackage[]
  tasks: TranscriptTask[]
  taskTotal: number
  orders: Order[]
  orderTotal: number
  account: CreditAccount | null
  ledger: LedgerRecord[]
  ledgerTotal: number
  selectedPackId: string
  currentOrderId: string | null
  loading: boolean
  tasksLoading: boolean
  ordersLoading: boolean
  creditsLoading: boolean
  error: string
  loadPackages: () => Promise<void>
  loadTasks: (query?: TaskListQuery) => Promise<void>
  loadOrders: (query?: OrderListQuery) => Promise<void>
  loadOrder: (orderId: string) => Promise<Order>
  loadCredits: (options?: {
    entryType?: string
    dateFrom?: string
    dateTo?: string
    page?: number
    pageSize?: number
  }) => Promise<void>
  createOrder: (packageId: string, channel: PaymentChannel) => Promise<Order>
  cancelOrder: (orderId: string) => Promise<void>
  repurchaseOrder: (orderId: string) => Promise<Order>
  payForOrder: (orderId: string, channel: PaymentChannel) => Promise<string>
  checkPaymentStatus: (orderId: string) => Promise<{ status: string; credited: boolean }>
  syncCasdoorPayments: () => Promise<{ synced: number; items: Array<Record<string, unknown>> }>
  clear: () => void
}

const errorMessage = (reason: unknown) =>
  reason instanceof Error ? reason.message : '请求失败，请稍后重试'

export const useBusinessStore = create<BusinessState>((set) => ({
  packages: [],
  tasks: [],
  taskTotal: 0,
  orders: [],
  orderTotal: 0,
  account: null,
  ledger: [],
  ledgerTotal: 0,
  selectedPackId: '',
  currentOrderId: null,
  loading: false,
  tasksLoading: true,
  ordersLoading: true,
  creditsLoading: true,
  error: '',
  loadPackages: async () => {
    set({ loading: true, error: '' })
    try {
      const packages = await commerceRepository.listPackages()
      set((state) => ({
        packages,
        selectedPackId: packages.some((item) => item.id === state.selectedPackId)
          ? state.selectedPackId
          : (packages.find((item) => item.tag === 'recommended')?.id ?? packages[0]?.id ?? ''),
      }))
    } catch (reason) {
      set({ error: errorMessage(reason) })
    } finally {
      set({ loading: false })
    }
  },
  loadTasks: async (query = {}) => {
    set({ loading: true, tasksLoading: true, error: '' })
    try {
      const result = await taskRepository.list(query)
      set({ tasks: result.items, taskTotal: result.total })
    } catch (reason) {
      set({ error: errorMessage(reason) })
    } finally {
      set({ loading: false, tasksLoading: false })
    }
  },
  loadOrders: async (query = {}) => {
    set({ loading: true, ordersLoading: true, error: '' })
    try {
      const result = await commerceRepository.listOrders(query)
      set({ orders: result.items, orderTotal: result.total })
    } catch (reason) {
      set({ error: errorMessage(reason) })
    } finally {
      set({ loading: false, ordersLoading: false })
    }
  },
  loadOrder: async (orderId) => {
    set({ loading: true, error: '' })
    try {
      const order = await commerceRepository.getOrder(orderId)
      set((state) => ({
        orders: [order, ...state.orders.filter((item) => item.id !== order.id)],
        currentOrderId: order.id,
      }))
      return order
    } catch (reason) {
      set({ error: errorMessage(reason) })
      throw reason
    } finally {
      set({ loading: false })
    }
  },
  loadCredits: async (options = {}) => {
    set({ loading: true, creditsLoading: true, error: '' })
    try {
      const [account, ledger] = await Promise.all([
        creditRepository.getBalance(),
        creditRepository.listLedger(options),
      ])
      set({ account, ledger: ledger.items, ledgerTotal: ledger.total })
    } catch (reason) {
      set({ error: errorMessage(reason) })
    } finally {
      set({ loading: false, creditsLoading: false })
    }
  },
  createOrder: async (packageId, channel) => {
    const order = await commerceRepository.createOrder(
      packageId,
      channel,
      requestKey('order-create'),
    )
    set((state) => ({
      orders: [order, ...state.orders.filter((item) => item.id !== order.id)],
      currentOrderId: order.id,
      selectedPackId: packageId,
    }))
    return order
  },
  cancelOrder: async (orderId) => {
    const order = await commerceRepository.cancelOrder(orderId)
    set((state) => ({
      orders: state.orders.map((item) => (item.id === order.id ? order : item)),
    }))
  },
  repurchaseOrder: async (orderId) => {
    const order = await commerceRepository.repurchaseOrder(orderId, requestKey('order-repurchase'))
    set((state) => ({
      orders: [order, ...state.orders.filter((item) => item.id !== order.id)],
      currentOrderId: order.id,
    }))
    return order
  },
  payForOrder: async (orderId, channel) => {
    const result = await commerceRepository.initiatePayment(
      orderId,
      channel,
      requestKey('order-pay'),
    )
    return result.payment_url
  },
  checkPaymentStatus: async (orderId) => {
    return commerceRepository.getPaymentStatus(orderId)
  },
  syncCasdoorPayments: async () => {
    return commerceRepository.syncCasdoorPayments()
  },
  clear: () =>
    set({
      packages: [],
      tasks: [],
      taskTotal: 0,
      orders: [],
      orderTotal: 0,
      account: null,
      ledger: [],
      ledgerTotal: 0,
      selectedPackId: '',
      currentOrderId: null,
      loading: false,
      tasksLoading: true,
      ordersLoading: true,
      creditsLoading: true,
      error: '',
    }),
}))
